const htmlToPlainText = (html) =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();

const uniquePreserveOrder = (items) => {
  const seen = new Set();
  const out = [];
  (items || []).forEach((raw) => {
    const key = String(raw || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(String(raw).trim());
  });
  return out;
};

const CALL_HEADING_RE = /^(need\s*to\s*call|call\s*reminders?|people\s*to\s*call|to\s*call)$/i;
const BUYING_HEADING_RE = /^(buying(\s*(items?|list|cart))?|buy(\s*(items?|list|cart))?|to\s*buy|shopping(\s*(items?|list|cart))?|grocery(\s*(items?|list|cart))?|groceries|cart)$/i;
const TASK_HEADING_RE = /^(tasks?(?:\s*(?:list|items?))?|to-?\s*dos?(?:\s*lists?)?|action\s*items?|todos?)\s*:?$/i;
const TASK_LINE_PREFIX_RE = /^task\s*[:\-–—]\s*(.+)$/i;

const cleanHeadingTitle = (raw) =>
  String(raw || '')
    .replace(/^[^\p{L}\p{N}]+/u, '')
    .replace(/[:\-–—]+\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();

const taskTitleFromLine = (raw) => {
  const text = String(raw || '').trim().replace(/^[-•*]+\s*/, '');
  const match = text.match(TASK_LINE_PREFIX_RE);
  const rest = String(match?.[1] || '').trim();
  return rest || null;
};

const taskPrefixedItemsFromHtml = (html) => {
  const out = [];
  const blockRe = /<(?:li|p|h[1-6])\b[^>]*>([\s\S]*?)<\/(?:li|p|h[1-6])>/gi;
  let match = blockRe.exec(html);
  while (match) {
    const title = taskTitleFromLine(htmlToPlainText(match[1] || ''));
    if (title) out.push(title);
    match = blockRe.exec(html);
  }
  return uniquePreserveOrder(out);
};

const headingHits = (html) => {
  const hits = [];
  const headingRe = /<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi;
  let match = headingRe.exec(html);
  while (match) {
    hits.push({
      start: match.index,
      end: match.index + match[0].length,
      title: cleanHeadingTitle(htmlToPlainText(match[1] || '')),
    });
    match = headingRe.exec(html);
  }
  const pRe = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
  match = pRe.exec(html);
  while (match) {
    const title = cleanHeadingTitle(htmlToPlainText(match[1] || ''));
    if (title && title.length <= 48
      && (CALL_HEADING_RE.test(title) || BUYING_HEADING_RE.test(title) || TASK_HEADING_RE.test(title))) {
      hits.push({
        start: match.index,
        end: match.index + match[0].length,
        title,
      });
    }
    match = pRe.exec(html);
  }
  return hits.sort((a, b) => a.start - b.start);
};

const listItemsFromHtml = (html, { checkboxesOnly = false } = {}) => {
  const out = [];
  const liRe = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
  let match = liRe.exec(html);
  while (match) {
    const full = match[0] || '';
    if (/data-list\s*=\s*["']checked["']/i.test(full)) {
      match = liRe.exec(html);
      continue;
    }
    const text = htmlToPlainText(match[1] || '');
    if (text) out.push(text);
    match = liRe.exec(html);
  }
  if (!checkboxesOnly && !out.length) {
    htmlToPlainText(html).split(/\n+/).forEach((line) => {
      const text = line.trim().replace(/^[-•*]\s*/, '');
      if (text) out.push(text);
    });
  }
  return uniquePreserveOrder(out);
};

const itemsUnderHeading = (html, titleRe, options) => {
  const matches = headingHits(html);
  const all = [];
  matches.forEach((hit, index) => {
    if (!titleRe.test(hit.title || '')) return;
    const start = hit.end;
    const end = index + 1 < matches.length ? matches[index + 1].start : html.length;
    if (end <= start) return;
    all.push(...listItemsFromHtml(html.slice(start, end), options));
  });
  return uniquePreserveOrder(all);
};

const cleanPersonName = (raw) =>
  String(raw || '')
    .trim()
    .replace(/^[-•*]+\s*/, '')
    .replace(/^(?:please\s+)?(?:call|phone|ring)\s+/i, '')
    .trim();

export const extractNeedToCallNamesFromNoteHtml = (html) => {
  if (!String(html || '').trim()) return [];
  return uniquePreserveOrder(
    itemsUnderHeading(html, CALL_HEADING_RE).map(cleanPersonName).filter(Boolean),
  );
};

export const extractBuyingItemsFromNoteHtml = (html) => {
  if (!String(html || '').trim()) return [];
  return itemsUnderHeading(html, BUYING_HEADING_RE, { checkboxesOnly: true });
};

export const extractTaskItemsFromNoteHtml = (html, exclude = []) => {
  if (!String(html || '').trim()) return [];
  const excluded = new Set((exclude || []).map((item) => String(item || '').trim().toLowerCase()).filter(Boolean));
  const dropBuying = (items) => items.filter((item) => !excluded.has(String(item || '').trim().toLowerCase()));
  return uniquePreserveOrder([
    ...dropBuying(itemsUnderHeading(html, TASK_HEADING_RE).map((item) => taskTitleFromLine(item) || item)),
    ...dropBuying(taskPrefixedItemsFromHtml(html)),
  ]);
};

export const personLabel = (person, fallback) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()
  || person?.username
  || person?.email
  || String(fallback || '').trim();
